/**
 * Extends the global Window interface to include Powerfront-specific properties.
 *
 * @typedef {Object} Window
 * @property {Array<any>} _inside - Array for Powerfront chat configuration
 * @property {Object} insideFrontInterface - Interface for Powerfront chat interactions
 */
declare global {
  interface Window {
    _inside: Array<any>
    insideFrontInterface: any
  }
}

/**
 * Nuxt plugin for integrating Powerfront live chat functionality.
 *
 * This plugin sets up the Powerfront chat interface, manages chat availability,
 * and handles dynamic chat initialization.
 *
 * @name powerfront
 * @async
 * @returns {void}
 * @example
 * // Chat availability is tracked via insideChat reactive state
 * // Automatically opens chat pane if requested before initialization
 */
export default defineNuxtPlugin({
  name: 'powerfront',
  async setup() {
    const { insideChat } = useInsideChat()

    /**
     * Configures the Powerfront chat setup and availability tracking.
     *
     * @function setupChat
     * @description Initializes chat configuration and sets up availability callback
     */
    const setupChat = () => {
      window._inside ||= []

      if (window._inside && Array.isArray(window._inside))
        window._inside.push({
          action: 'bind',
          name: 'chatavailable',
          /**
           * Callback to handle chat availability status.
           *
           * @param {boolean} available - Indicates if chat is currently available
           */
          callback: (available: boolean) => {
            try {
              insideChat.value.isLoaded = true
              insideChat.value.isAvailable = available

              if (available && insideChat.value.hasBeenRequestedBeforeInit) {
                // @ts-ignore
                window.insideFrontInterface.openChatPane()
              }
            } catch (error) {
              console.error('Error in livechat callback: ', error)
            }
          },
        })
    }

    setupChat()

    useScript({
      async: true,
      src: 'https://cdn8.eu.inside.chat/gtm/IN-1001177-NEW/include.js',
    })
  },
})
